import { useSyncExternalStore } from "react";

/**
 * The window's current inner width, as a subscription.
 *
 * Both resizable columns derive their maximum from the window, so the maximum
 * has to be a *subscription* rather than a value read once at mount: a width
 * that was legal on an external monitor has to be re-clamped when the same
 * window is reopened on a laptop, and a render-time snapshot would never
 * refresh. `useSyncExternalStore` rather than `useState` + an effect because a
 * clamped drag ends with an unchanged width, which skips the re-render that
 * would otherwise be the thing keeping a snapshot current.
 */
function subscribe(onChange: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener("resize", onChange);
  return () => window.removeEventListener("resize", onChange);
}

function getSnapshot(): number {
  return window.innerWidth;
}

/**
 * A fixed desktop-ish width on the server. Nothing is measurable there, and the
 * bounds this feeds re-clamp on the client's first real layout anyway.
 */
function getServerSnapshot(): number {
  return 1280;
}

export function useViewportWidth(): number {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
